import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";

interface Props { payload: VisualPayload }

// ---------------------------------------------------------------------------
// Section sub-renderers
// ---------------------------------------------------------------------------

/** Arrow connector between two Pokémon in an evolution chain. */
function EvoArrow() {
  return (
    <div className="flex items-center justify-center my-1">
      <svg className="w-4 h-4 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );
}

/** Pill badges for evolution conditions (item, buddy, trade, and the other requirement phrases). */
function ConditionBadges({ sec }: { sec: Record<string, unknown> }) {
  const badges: string[] = [];
  if (sec.requires_item) badges.push(`🔮 ${sec.requires_item as string}`);
  if (sec.requires_buddy_km) badges.push(`🚶 ${sec.requires_buddy_km as number} km buddy`);
  if (sec.requires_trade) badges.push("🔄 Trade");
  // The other conditions as phrases from the backend (plan phase 4): "at night", "near a Mossy Lure Module" …
  if (Array.isArray(sec.requirements)) (sec.requirements as string[]).forEach((r) => badges.push(r));
  if (!badges.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {badges.map((b) => (
        <span key={b} className="text-[10px] bg-white/[0.06] border border-white/[0.08] rounded-full px-2 py-0.5 text-white/60">
          {b}
        </span>
      ))}
    </div>
  );
}

/** A single Pokémon name pill in the chain. */
function PokemonPill({ name, highlight = false }: { name: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl px-3 py-2 text-sm font-medium ${
      highlight
        ? "bg-scout-yellow/10 border border-scout-yellow/30 text-scout-yellow"
        : "bg-white/[0.05] border border-white/[0.08] text-white"
    }`}>
      {name}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: evolution_info — individual "X → Y" step
// ---------------------------------------------------------------------------

function EvoStepSection({ sec }: { sec: Record<string, unknown> }) {
  return (
    <div className="space-y-1">
      <EvoArrow />
      <div className="rounded-xl border border-white/[0.07] p-3 space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-white">{sec.to_name as string}</span>
          {sec.candy_cost != null && (
            <span className="text-xs text-white/50 tabular-nums">
              🍬 {(sec.candy_cost as number).toLocaleString()} candy
            </span>
          )}
        </div>
        <ConditionBadges sec={sec} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: evolution_info — pre-evolution ("X evolves FROM Y")
// ---------------------------------------------------------------------------

function PreEvoSection({ sec }: { sec: Record<string, unknown> }) {
  return (
    <div className="space-y-1 mb-2">
      <PokemonPill name={sec.from_name as string} />
      <EvoArrow />
      <PokemonPill name={sec.to_name as string} highlight />
      <p className="text-[10px] text-white/30 mt-1">Evolved from {sec.from_name as string}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: candy_cost — evolution_costs list
// ---------------------------------------------------------------------------

function EvoCostsSection({ sec }: { sec: Record<string, unknown> }) {
  const evolutions = (sec.evolutions as Array<Record<string, unknown>>) ?? [];
  return (
    <div className="space-y-2">
      <p className="text-[10px] text-white/30 uppercase tracking-wide">Evolution Costs</p>
      {evolutions.map((e, i) => (
        <div key={i} className="rounded-xl border border-white/[0.07] p-3 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-white">{e.to_name as string}</span>
            {e.candy_cost != null && (
              <span className="text-xs text-white/50 tabular-nums">
                🍬 {(e.candy_cost as number).toLocaleString()} candy
              </span>
            )}
          </div>
          <ConditionBadges sec={e} />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: candy_cost — powerup_milestones table
// ---------------------------------------------------------------------------

function PowerupMilestonesSection({ sec }: { sec: Record<string, unknown> }) {
  const milestones = (sec.milestones as Array<Record<string, unknown>>) ?? [];
  return (
    <div className="space-y-2 mt-4">
      <p className="text-[10px] text-white/30 uppercase tracking-wide">Power-Up Costs</p>
      <p className="text-[10px] text-white/20">{sec.note as string}</p>
      <div className="rounded-xl border border-white/[0.07] overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-4 text-[10px] text-white/30 uppercase tracking-wide bg-white/[0.03] px-3 py-2">
          <span>Level</span>
          <span className="text-right">Stardust</span>
          <span className="text-right">Candy</span>
          <span className="text-right">XL</span>
        </div>
        {/* Rows */}
        {milestones.map((m, i) => (
          <div
            key={i}
            className={`grid grid-cols-4 text-xs px-3 py-2 ${i % 2 === 0 ? "bg-white/[0.02]" : ""}`}
          >
            <span className="font-medium text-scout-yellow">Lv {m.to_level as number}</span>
            <span className="text-right text-white/70 tabular-nums">
              {(m.stardust as number).toLocaleString()}
            </span>
            <span className="text-right text-white/70 tabular-nums">{m.candy as number}</span>
            <span className={`text-right tabular-nums ${(m.xl_candy as number) > 0 ? "text-purple-400" : "text-white/20"}`}>
              {(m.xl_candy as number) > 0 ? (m.xl_candy as number) : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function EvolutionCard({ payload }: Props) {
  const sections = payload.sections;

  // Determine which mode we're rendering
  const hasPreEvo       = sections.some((s) => s.section === "pre_evolution");
  const evoSteps        = sections.filter((s) => s.section === "evolution");
  const noEvolutions    = sections.find((s) => s.section === "no_evolutions" || s.section === "no_evolution");
  const evoCosts        = sections.find((s) => s.section === "evolution_costs");
  const powerupMilest   = sections.find((s) => s.section === "powerup_milestones");
  const notFound        = sections.find((s) => s.section === "not_found");
  const preEvoSec       = sections.find((s) => s.section === "pre_evolution");
  const fromName        = preEvoSec ? (preEvoSec.from_name as string) : null;

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      {/* Not found */}
      {notFound && (
        <p className="text-sm text-white/40">{notFound.reason as string}</p>
      )}

      {/* evolution_info: pre-evolution context */}
      {hasPreEvo && preEvoSec && (
        <PreEvoSection sec={preEvoSec as Record<string, unknown>} />
      )}

      {/* evolution_info: forward evolution steps */}
      {!hasPreEvo && fromName == null && evoSteps.length > 0 && (
        <div className="mb-2">
          <PokemonPill name={payload.title.split(" —")[0]} highlight />
        </div>
      )}
      {evoSteps.map((sec, i) => (
        <EvoStepSection key={i} sec={sec as Record<string, unknown>} />
      ))}

      {/* evolution_info: no evolutions */}
      {noEvolutions && evoSteps.length === 0 && !evoCosts && (
        <p className="text-sm text-white/40">{noEvolutions.note as string}</p>
      )}

      {/* candy_cost: evolution costs list */}
      {evoCosts && (
        <EvoCostsSection sec={evoCosts as Record<string, unknown>} />
      )}

      {/* candy_cost: no evolution note */}
      {noEvolutions && evoCosts == null && powerupMilest && (
        <p className="text-sm text-white/50 mb-2">{noEvolutions.note as string}</p>
      )}

      {/* candy_cost: power-up milestones table */}
      {powerupMilest && (
        <PowerupMilestonesSection sec={powerupMilest as Record<string, unknown>} />
      )}

      {/* Truly empty sections (no data at all) */}
      {sections.length === 0 && (
        <p className="text-sm text-white/40">No evolution data available.</p>
      )}
    </CardShell>
  );
}
